import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { formatMoney } from '../../types/billing';
import { TopUpQuote, TopUpView, walletTopupsService } from '../../api/services/walletTopups.service';
import { actingRole } from '../../utils/roleGrants';
import {
  TOPUP_STATE_LABELS, amountError, awaitingPayment, canCreateTopUp, defaultAmount, formatTopUpTime,
  invoiceLabel, isAmountRefusal, isTopUpDisabled, taxCaption, topUpStateClass,
} from './topUpRules';

/**
 * One-time wallet top-up for ONE prepaid organisation: a live quote, a payable link,
 * and the history. Ships dark behind a server flag; while it is off the card says so
 * quietly. The wallet is credited only when the payment gateway confirms the payment.
 */
interface Props {
  organizationId: string;
  /** The wallet's currency, for the first amount before a quote names it. */
  currency?: string | null;
}

const card = 'bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4';
const h2 = 'text-lg font-semibold text-gray-900 dark:text-white';
const muted = 'text-sm text-gray-500 dark:text-gray-400';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap text-left';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';
const tdNum = `${td} text-right tabular-nums`;
const badge = 'inline-flex px-2 py-0.5 text-xs font-medium rounded-full';
const inputCls =
  'w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm';
const primaryBtn = 'px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50';
const secondaryBtn =
  'inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50';

const POLL_TIMES = 5;
const POLL_EVERY_MS = 4000;
const DEBOUNCE_MS = 400;

const TestBadge: React.FC = () => (
  <span className={`${badge} bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300`} data-testid="test-mode">
    Test mode — no real money
  </span>
);

const TopUpPanel: React.FC<Props> = ({ organizationId, currency }) => {
  const auth = useAuth();
  const mayCreate = canCreateTopUp(actingRole(auth));

  const [amount, setAmount] = useState(String(defaultAmount(currency)));
  const [quote, setQuote] = useState<TopUpQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [link, setLink] = useState<TopUpView | null>(null);
  const [copied, setCopied] = useState(false);
  const [polling, setPolling] = useState(false);
  const [history, setHistory] = useState<TopUpView[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // The list view carries no url, so a still-payable row fetches its own on request.
  const [rowUrls, setRowUrls] = useState<Record<number, string | null>>({});
  const clamped = useRef(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const loadHistory = useCallback(async () => {
    const r = await walletTopupsService.list(organizationId, 0, 10);
    if (!alive.current) return;
    if (r.success) {
      setHistory(r.data.content || []);
      setHistoryError(null);
    } else if (r.status !== 404) {
      setHistoryError(r.error);
    }
  }, [organizationId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // A new organisation starts over.
  useEffect(() => {
    clamped.current = false;
    setLink(null);
    setConfirming(false);
    setAmount(String(defaultAmount(currency)));
  }, [organizationId, currency]);

  // The live quote, debounced. A blank or malformed amount is not sent.
  const localError = amountError(amount, quote?.minCredit, quote?.maxCredit);
  const formatOk = amountError(amount) === null;
  useEffect(() => {
    if (!formatOk) return undefined;
    let live = true;
    const t = setTimeout(async () => {
      setQuoting(true);
      const r = await walletTopupsService.quote(Number(amount), organizationId);
      if (!live || !alive.current) return;
      setQuoting(false);
      if (!r.success) {
        setQuote(null);
        setQuoteError(r.error);
        return;
      }
      setQuoteError(null);
      setQuote(r.data);
      // Prefill inside the bounds once, from the first quote that names them.
      if (!clamped.current && !isTopUpDisabled(r.data)) {
        clamped.current = true;
        const fitted = defaultAmount(r.data.currency || currency, r.data.minCredit, r.data.maxCredit);
        if (fitted !== Number(amount) && isAmountRefusal(r.data)) setAmount(String(fitted));
      }
    }, DEBOUNCE_MS);
    return () => { live = false; clearTimeout(t); };
  }, [amount, organizationId, formatOk, currency]);

  const cur = quote?.currency || currency || 'INR';
  const disabled = isTopUpDisabled(quote);
  const priced = !!quote && !quote.refusal && quote.charge !== null;
  // The quote on screen must be for the amount in the box, so the link is for what was shown.
  const current = priced && Number(quote!.credit) === Number(amount);
  const canSubmit = mayCreate && current && !localError && !quoting;

  const create = async () => {
    setCreating(true);
    setCreateError(null);
    const r = await walletTopupsService.create(Number(amount), organizationId);
    if (!alive.current) return;
    setCreating(false);
    setConfirming(false);
    if (r.success) {
      setLink(r.data);
      loadHistory();
    } else {
      setCreateError(r.error);
    }
  };

  const copy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => alive.current && setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  /** Asks a few times, then stops: the webhook usually lands within a minute. */
  const checkStatus = async () => {
    if (!link) return;
    setPolling(true);
    let latest: TopUpView = link;
    for (let i = 0; i < POLL_TIMES; i++) {
      const r = await walletTopupsService.get(link.id, organizationId);
      if (!alive.current) return;
      if (r.success) {
        // The view drops the url once it cannot be paid; keep the one we were given while it can.
        latest = { ...r.data, url: r.data.url ?? (awaitingPayment(r.data.state) ? link.url : null) };
        setLink(latest);
      }
      if (!awaitingPayment(latest.state)) break;
      if (i < POLL_TIMES - 1) {
          await new Promise((res) => setTimeout(res, POLL_EVERY_MS));
        if (!alive.current) return;
      }
    }
    setPolling(false);
    loadHistory();
  };

  const showRowLink = async (id: number) => {
    const r = await walletTopupsService.get(id, organizationId);
    if (!alive.current) return;
    setRowUrls((m) => ({ ...m, [id]: r.success && r.data.url ? r.data.url : null }));
    if (r.success && !awaitingPayment(r.data.state)) loadHistory();
  };

  const historyTable = history.length > 0 && (
    <div>
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Top-up history</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" aria-label="Top-up history">
          <thead>
            <tr>
              <th className={th}>Date</th>
              <th className={`${th} text-right`}>Credit</th>
              <th className={`${th} text-right`}>Tax</th>
              <th className={`${th} text-right`}>Total</th>
              <th className={th}>State</th>
              <th className={th}>Invoice</th>
              <th className={th}>Link</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {history.map((t) => {
              const href = t.url || rowUrls[t.id] || undefined;
              return (
              <tr key={t.id}>
                <td className={td}>{formatTopUpTime(t.createdAt)}</td>
                <td className={tdNum}>{formatMoney(t.credit, t.currency || cur)}</td>
                <td className={tdNum}>{formatMoney(t.tax, t.currency || cur)}</td>
                <td className={tdNum}>{formatMoney(t.charge, t.currency || cur)}</td>
                <td className={td}>
                  <span className={`${badge} ${topUpStateClass(t.state)}`}>{TOPUP_STATE_LABELS[t.state] || t.state}</span>
                  {t.mode === 'TEST' && <span className="ml-1 text-xs text-amber-700 dark:text-amber-400">test</span>}
                </td>
                <td className={td}>{invoiceLabel(t.invoiceStatus, t.invoiceNumber, t.taxDocumentNumber)}</td>
                <td className={td}>
                  {href && awaitingPayment(t.state) ? (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline">
                      Pay <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : awaitingPayment(t.state) && rowUrls[t.id] !== null ? (
                    <button type="button" className="text-indigo-600 dark:text-indigo-400 hover:underline text-sm"
                            onClick={() => showRowLink(t.id)}
                            title={t.expiresAt ? `Payable until ${formatTopUpTime(t.expiresAt)} IST` : undefined}>
                      Show link
                    </button>
                  ) : '-'}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (disabled) {
    return (
      <section className={card} aria-label="Add money">
        <h2 className={h2}>Add money</h2>
        <p className={muted} data-testid="topup-unavailable">
          Online top-up isn't available yet. To add credit, contact your account manager.
        </p>
        {historyTable}
      </section>
    );
  }

  return (
    <section className={card} aria-label="Add money">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className={h2}>Add money</h2>
        {quote?.mode === 'TEST' && <TestBadge />}
      </div>
      <p className={muted}>
        Pay once by card, UPI or netbanking. The wallet is credited only after the payment gateway
        confirms the payment, which can take a minute.
      </p>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="topup-amount">
            Credit to add ({cur})
          </label>
          <input id="topup-amount" className={inputCls} inputMode="decimal" value={amount}
                 onChange={(e) => { setAmount(e.target.value); setConfirming(false); setCreateError(null); }} />
          {quote && quote.minCredit !== null && quote.maxCredit !== null && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formatMoney(quote.minCredit, cur, 0)} to {formatMoney(quote.maxCredit, cur, 0)}
            </p>
          )}
        </div>
        {quoting && <Loader2 className="w-4 h-4 animate-spin text-gray-400 mb-3" aria-label="Pricing" />}
      </div>

      {localError && <p className="text-sm text-red-600 dark:text-red-400">{localError}</p>}
      {!localError && quote?.refusal && (
        <p className={isAmountRefusal(quote) ? 'text-sm text-red-600 dark:text-red-400' : 'text-sm text-amber-700 dark:text-amber-400'}
           data-testid="topup-refusal">
          {quote.refusal}
        </p>
      )}
      {quoteError && <p className="text-sm text-red-600 dark:text-red-400">{quoteError}</p>}

      {priced && !localError && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="topup-quote">
          <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-xs uppercase text-gray-500 dark:text-gray-400">Wallet credit</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{formatMoney(quote!.credit, cur)}</div>
          </div>
          <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-xs uppercase text-gray-500 dark:text-gray-400">{taxCaption(quote!)}</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{formatMoney(quote!.tax, cur)}</div>
            {quote!.taxNote && <div className="text-xs text-gray-500 dark:text-gray-400">{quote!.taxNote}</div>}
          </div>
          <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-xs uppercase text-gray-500 dark:text-gray-400">Total charged</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{formatMoney(quote!.charge, cur)}</div>
            {quote!.gatewayLabel && <div className="text-xs text-gray-500 dark:text-gray-400">via {quote!.gatewayLabel}</div>}
          </div>
        </div>
      )}

      {!link && !confirming && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={primaryBtn} disabled={!canSubmit}
                  onClick={() => setConfirming(true)}>
            Create payment link
          </button>
          {!mayCreate && (
            <span className={muted} data-testid="ask-owner">Ask your account owner to top up.</span>
          )}
        </div>
      )}

      {confirming && priced && (
        <div className="rounded-md border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-3 space-y-2"
             data-testid="topup-confirm">
          <p className="text-sm text-gray-900 dark:text-white">
            Create a payment link for {formatMoney(quote!.charge, cur)}? Once paid, {formatMoney(quote!.credit, cur)} is
            added to the wallet.
            {quote!.mode === 'TEST' ? ' This is a test-mode link: no real money moves.' : ''}
          </p>
          <div className="flex gap-2">
            <button type="button" className={primaryBtn} onClick={create} disabled={creating}>
              {creating ? 'Creating…' : 'Yes, create the link'}
            </button>
            <button type="button" className={secondaryBtn} onClick={() => setConfirming(false)} disabled={creating}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {createError && <p className="text-sm text-red-600 dark:text-red-400" role="alert">{createError}</p>}

      {link && (
        <div className="rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3 space-y-2"
             data-testid="topup-link">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`${badge} ${topUpStateClass(link.state)}`}>{TOPUP_STATE_LABELS[link.state] || link.state}</span>
            {link.mode === 'TEST' && <TestBadge />}
            <span className="text-sm text-gray-900 dark:text-white">
              {formatMoney(link.charge, link.currency || cur)} for {formatMoney(link.credit, link.currency || cur)} credit
            </span>
          </div>
          {link.url && awaitingPayment(link.state) && (
            <div className="flex flex-wrap items-center gap-2">
              <a href={link.url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline break-all">
                Open the payment page <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button type="button" className={secondaryBtn} onClick={copy}>
                <Copy className="w-3.5 h-3.5" /> {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          )}
          {link.expiresAt && awaitingPayment(link.state) && (
            <p className={muted}>The link can be paid until {formatTopUpTime(link.expiresAt)} IST.</p>
          )}
          <p className={muted}>
            {link.credited
              ? 'Payment confirmed: the wallet has been credited.'
              : 'The wallet is credited only after the payment gateway confirms the payment. This can take a minute after paying.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {!link.credited && (
              <button type="button" className={secondaryBtn} onClick={checkStatus} disabled={polling}>
                {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {polling ? 'Checking…' : 'Check status'}
              </button>
            )}
            <button type="button" className={secondaryBtn} onClick={() => { setLink(null); setCopied(false); }} disabled={polling}>
              Done
            </button>
          </div>
        </div>
      )}

      {historyError && <p className="text-sm text-red-600 dark:text-red-400">{historyError}</p>}
      {historyTable}
    </section>
  );
};

export default TopUpPanel;
